import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Building2 } from "lucide-react";
import { cn } from "src/lib/utils";
import { Button } from "src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "src/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "src/components/ui/avatar";
import { useAuth } from "@clerk/clerk-react"; // Import Clerk hook
import { ArrowRight } from "lucide-react"
import { LogOut } from 'lucide-react';
import { useApiFetch } from "src/lib/api";

export function OrgSelector({ onOrgSelect, className }) {
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState();
  const apiFetch = useApiFetch();

  const handleOrgSelect = (org) => {
    setSelectedOrg(org);
    setOpen(false);
    onOrgSelect({id: org.id, role: org.role});
  };
  
  useEffect(() => {
    async function fetchOrganizations() {
      try {
        const data = await apiFetch("/auth/auth-organizations");
        if (!data) return;
        const organizations = data?.organizations ?? [];
        const firstOrg = organizations?.[0];
        setOrgs(organizations);
        setSelectedOrg(firstOrg);
        if (firstOrg) {
          onOrgSelect({id: firstOrg?.id, role: firstOrg?.role});
        }
        
      } catch (error) {
        console.error("Error fetching active assets:", error);
      }
    }

    fetchOrganizations();
  }, []);

  return (
    <div className={cn("flex flex-col space-y-1", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Select organization"
            className={cn(
              "w-full justify-between px-3 py-6 text-left font-normal",
              !selectedOrg && "text-muted-foreground"
            )}
          >
            <div className="flex items-center gap-3">
              {selectedOrg ? (
                <>
                  <Avatar className="h-8 w-8">
                    <AvatarImage
                      src={selectedOrg.image || "/placeholder.svg"}
                      alt={selectedOrg.name}
                    />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {selectedOrg.name?.substring(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{selectedOrg.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {selectedOrg.role}
                    </span>
                  </div>
                </>
              ) : (
                <Building2 className="mr-2 h-5 w-5" />
              )}
            </div>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0">
          <Command>
            <CommandInput placeholder="Search organization..." />
            <CommandList>
              <CommandEmpty>No organizations found.</CommandEmpty>
              <CommandGroup heading="Organizations">
                {((orgs ?? []).map((org) => (
                  <CommandItem
                    key={org.id}
                    onSelect={() => handleOrgSelect(org)}
                    className="flex items-center gap-3 px-4 py-3"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={org.image || "/placeholder.svg"}
                        alt={org.name}
                      />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {org.name?.substring(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{org.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {org.role}
                      </span>
                    </div>
                  </CommandItem>
                )))}
              </CommandGroup>
              <CommandSeparator />
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Componente de ejemplo para mostrar cómo usar OrgSelector
export default function OrgSelectorDemo() {
  const navigate = useNavigate(); // Hook para redirección
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [selectedOrgRole, setSelectedOrgRole] = useState(null);
  const { signOut } = useAuth();

  const apiFetch = useApiFetch();

  const handleOrgSelect = async () => {
    if (!selectedOrgId) {
    console.warn("No organization selected");
    return;
    }
    console.log(`[INFO] Selected organization ID to access: ${selectedOrgId}`);
    try {
      const data = await apiFetch("/auth/auth", {
        method: "POST",
        body: { orgId: selectedOrgId, role: selectedOrgRole },
      });
      if (!data) return;
      navigate("/index");
    } catch (error) {
      console.error("Backend error: ", error);
    }
  };

  const handleLogOut = async () => {
    await signOut();
    navigate("/sign-in");
  };
  
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-white p-6 shadow-sm">
        <h2 className="mb-6 text-xl font-semibold text-center">
          Select Organization
        </h2>
        <OrgSelector onOrgSelect={({ id, role }) => {
          setSelectedOrgId(id);
          setSelectedOrgRole(role);
        }} />
        <div className="flex flex-col justify-between">
          <button
          onClick={handleOrgSelect}
          isLoading={false}
          type={"submit"}
          className="group w-full flex items-center justify-center gap-2 bg-primary text-white py-2 px-2 mt-2 rounded-md shadow-sm transition-all duration-200 hover:bg-primary/85 hover:shadow-md active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <span>Continue</span>
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <Button
            onClick={handleLogOut}
            isLoading={false}
            variant={"ghost"}
            className=" flex items-center justify-center gap-2 p-3 mt-2 rounded-md transition-colors" 
          >
            <LogOut  size={16} />
            <span>Sign out</span>
          </Button> 
        </div>
      </div>
    </div>
  );
}